// Helpers over the /plan-catalog response:
// { families: [{ family, label, variants: [{ variant, label,
//     durations: { monthly: { key, price }, quarterly: {...}, yearly: {...} } }] }] }

export const ADDON_FAMILIES = ["flow_addon", "team_members", "ai_messages"];

export const familyEntry = (catalog, family) =>
  ((catalog && catalog.families) || []).find((f) => f.family === family) || null;

export const variantsOf = (catalog, family) => {
  const fe = familyEntry(catalog, family);
  return fe ? fe.variants : [];
};

export const familyLabel = (catalog, family) => {
  const fe = familyEntry(catalog, family);
  return fe ? fe.label : family;
};

// (family, variant, duration) -> V4_PLANS key, or null.
export const keyFor = (catalog, family, variant, duration) => {
  const v = variantsOf(catalog, family).find((x) => x.variant === variant);
  return (v && v.durations && v.durations[duration] && v.durations[duration].key) || null;
};

// Reverse: which { family, variant, label, duration } a V4_PLANS key belongs to.
export const findByKey = (catalog, key) => {
  for (const f of (catalog && catalog.families) || []) {
    for (const v of f.variants || []) {
      for (const [duration, d] of Object.entries(v.durations || {})) {
        if (d.key === key) {
          return { family: f.family, variant: v.variant, label: v.label, duration };
        }
      }
    }
  }
  return null;
};

// Variants of a family that HAVE a key at the given duration.
export const variantsAtDuration = (catalog, family, duration) =>
  variantsOf(catalog, family).filter(
    (v) => v.durations && v.durations[duration] && v.durations[duration].key
  );

// First plan key at a duration (used to seed a new subscription).
export const firstPlanKeyAt = (catalog, duration) => {
  const vs = variantsAtDuration(catalog, "plan", duration);
  return vs.length ? vs[0].durations[duration].key : null;
};

// ---- unit metadata ----

// "flow" | "seat" | "message" | null (plans are not sold by quantity).
export const unitOf = (catalog, family) => {
  const fe = familyEntry(catalog, family);
  return fe ? fe.unit : null;
};

// How many units ONE copy of this key gives: 10 for the 10-flow pack, 1 for a
// seat, 3500 for the 3.5K AI pack. null for plans and unknown keys.
export const packSizeOfKey = (catalog, key) => {
  for (const f of (catalog && catalog.families) || []) {
    for (const v of f.variants || []) {
      for (const d of Object.values(v.durations || {})) {
        if (d.key === key) return v.size == null ? null : v.size;
      }
    }
  }
  return null;
};

// Catalog list price for a key, in rupees. Stored as 1/100,000 of a rupee, so
// 250000000 is 2500.
export const catalogPriceOfKey = (catalog, key, currency = "INR") => {
  for (const f of (catalog && catalog.families) || []) {
    for (const v of f.variants || []) {
      for (const d of Object.values(v.durations || {})) {
        if (d.key === key) {
          const p = d.price && d.price[currency];
          return typeof p === "number" ? p / 100000 : null;
        }
      }
    }
  }
  return null;
};

// Total units a family contributes, across however many entries it has.
// Flows: 1 × 5 + 8 × 10 = 85.
export const totalUnitsOf = (catalog, addOns, family) =>
  (addOns || [])
    .filter((a) => a.family === family)
    .reduce((sum, a) => sum + (packSizeOfKey(catalog, a.key) || 0) * (Number(a.quantity) || 0), 0);

// Flows sell as a small base pack plus repeats of a larger one. Returns the two
// variants ordered by size, or null if the catalog isn't shaped that way (in
// which case the stepper can't be built).
export const flowPacksAt = (catalog, duration) => {
  const vs = variantsAtDuration(catalog, "flow_addon", duration)
    .filter((v) => Number(v.size) > 0)
    .sort((a, b) => a.size - b.size);
  if (vs.length < 2) return null;
  const last = vs[vs.length - 1];
  return {
    base: { variant: vs[0].variant, size: vs[0].size, key: vs[0].durations[duration].key },
    increment: { variant: last.variant, size: last.size, key: last.durations[duration].key },
  };
};

// Build the flow add-on entries for k increment packs. Always exactly one base
// pack, so the reachable totals are base, base+inc, base+2*inc, …
export const flowAddOnsFor = (packs, k) => {
  if (!packs) return [];
  const out = [{ family: "flow_addon", key: packs.base.key, quantity: 1 }];
  if (k > 0) out.push({ family: "flow_addon", key: packs.increment.key, quantity: k });
  return out;
};

// How many increment packs the current entries represent.
export const incrementPackCount = (packs, addOns) => {
  if (!packs) return 0;
  const inc = (addOns || []).find((a) => a.key === packs.increment.key);
  return inc ? Number(inc.quantity) || 0 : 0;
};
